import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Sparkles, ArrowRight, Target, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";

export function OnboardingBanner({ favoriteName, onChosen, onPredictNow }) {
  const [teams, setTeams] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (favoriteName) return;
    api.get("/teams").then(({ data }) => setTeams(data.teams)).catch(() => {});
  }, [favoriteName]);

  const grouped = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = term
      ? teams.filter((t) => t.name.toLowerCase().includes(term) || (t.short || "").toLowerCase().includes(term))
      : teams;
    return list.reduce((acc, tm) => {
      (acc[tm.league_name] = acc[tm.league_name] || { emoji: tm.league_emoji, items: [] }).items.push(tm);
      return acc;
    }, {});
  }, [teams, q]);

  const pick = async (teamId) => {
    setBusy(true);
    setOpen(false);
    try {
      const { data } = await api.put("/me/favorite-team", { team_id: teamId });
      onChosen(data.team_id, data.league_id, data.name);
      toast.success(`${data.name} — good shout. Let's call your opening week.`);
    } catch {
      toast.error("Could not save your club. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="onboarding-banner" className="relative overflow-hidden mb-8 border-2 border-primary bg-primary text-primary-foreground">
      <div className="flex flex-col md:flex-row md:items-center gap-4 px-5 py-4">
        <div className="flex-1">
          <p className="font-data text-[10px] uppercase tracking-[0.25em] mb-1 flex items-center gap-2">
            <Sparkles size={13} /> {favoriteName ? "Almost there" : "Welcome to GAFFER"}
          </p>
          <h3 className="font-display text-2xl md:text-3xl uppercase leading-none">
            {favoriteName ? `Call ${favoriteName}'s opening week` : "Who do you support?"}
          </h3>
          <p className="text-sm mt-1 text-primary-foreground/85 max-w-xl">
            {favoriteName
              ? "Predict the Week 1 scoreline and you're off — your forecast starts tracking against the real results straight away."
              : "Pick your club and we'll drop you straight into predicting your team's opening weekend."}
          </p>
        </div>
        <div className="shrink-0">
          {favoriteName ? (
            <Button onClick={onPredictNow} data-testid="onboarding-predict-btn"
              className="bg-background text-foreground border-2 border-background rounded-none hover:bg-transparent hover:text-primary-foreground hover:border-primary-foreground">
              <Target size={16} className="me-2" /> Predict Week 1 <ArrowRight size={16} className="ms-2" />
            </Button>
          ) : (
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" disabled={busy} data-testid="onboarding-club-select"
                  className="w-64 justify-between bg-background text-foreground border-2 border-background rounded-none h-11 font-semibold hover:bg-background/90">
                  {busy ? "Saving…" : "Search your club"}
                  <ChevronsUpDown size={16} className="opacity-60" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 p-0 bg-popover text-foreground border-2 border-foreground rounded-none">
                <Command shouldFilter={false} className="bg-popover">
                  <CommandInput value={q} onValueChange={setQ} placeholder="Type a club name…" data-testid="onboarding-club-search" className="font-data" />
                  <CommandList className="max-h-80">
                    <CommandEmpty className="py-6 text-center text-sm text-muted-foreground">No club found.</CommandEmpty>
                    {Object.entries(grouped).map(([lname, g]) => (
                      <CommandGroup key={lname} heading={`${g.emoji} ${lname}`}>
                        {g.items.map((tm) => (
                          <CommandItem key={tm.id} value={tm.id}
                            onSelect={() => pick(tm.id)} data-testid={`club-opt-${tm.id}`}
                            className="rounded-none cursor-pointer">
                            {tm.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    ))}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>
    </div>
  );
}
